/**
 * SystemDashboard · SFUI-003 Phase 6 · collapsible terminal-style chamber.
 *
 * Three sub-panels: NODE STATUS · AVAILABLE MINDS · FEDERATION WIRE.
 * Default collapsed (summary). Click header to expand into full terminal.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { RECYCLER_FACE_URL, RECYCLER_ALT, RECYCLER_FILTER } from "@/lib/personaFaces";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NODES_SEED = [
  { name: "Core",     domain: "nextxus.online",  status: "live"    },
  { name: "KEYS",     domain: "nextxus.store",   status: "wire"    },
  { name: "Oracle",   domain: "nextxus.one",     status: "amber"   },
  { name: "Helper",   domain: "nextxus.help",    status: "live"    },
  { name: "Studio",   domain: "nextxus.studio",  status: "live"    },
  { name: "Space",    domain: "nextxus.space",   status: "live"    },
  { name: "Digital",  domain: "nextxus.digital", status: "live"    },
];

const MINDS_SEED = [
  { name: "Rex",     tag: "Concierge",    color: "#00F0FF" },
  { name: "Xavier",  tag: "Teacher",      color: "#FFB53E" },
  { name: "Aria",    tag: "Continuity",   color: "#E040FB" },
  { name: "Oracle",  tag: "Foresight",    color: "#FFB53E" },
  { name: "Axiom",   tag: "Truth Engine", color: "#00F0FF" },
];

const STATUS_DOT = {
  live:  { color: "#2BFF8A", label: "LIVE"  },
  wire:  { color: "#00F0FF", label: "WIRE"  },
  amber: { color: "#FFB53E", label: "DNS"   },
  red:   { color: "#FF4D6D", label: "DOWN"  },
};

export default function SystemDashboard() {
  // Architect mandate Feb-2026: open by default so The Recycler face
  // and live ops indicators are visible without an extra click.
  const [open, setOpen] = useState(true);
  const [wire, setWire] = useState([]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    axios.get(`${API}/wire/library?limit=3`).then(r => {
      if (!cancelled) setWire(r.data?.items || []);
    }).catch(() => { /* fallback to empty */ });
    return () => { cancelled = true; };
  }, [open]);

  const ts = useMemo(() => new Date().toISOString().replace("T", " ").slice(0, 19) + " Z", [open]);

  return (
    <section
      id="chamber-dashboard"
      className="rounded-2xl mb-6"
      data-testid="system-dashboard"
      style={{
        background: "rgba(5, 5, 18, 0.82)",
        backdropFilter: "blur(12px)",
        border: "1px dashed rgba(0, 240, 255, 0.45)",
        boxShadow: "0 0 28px rgba(0, 240, 255, 0.18), inset 0 1px 0 rgba(255,255,255,0.04)",
      }}
    >
      {/* Header (click to toggle) */}
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="system-dashboard-toggle"
        className="w-full flex items-center justify-between px-5 sm:px-7 py-4 text-left"
        style={{ borderBottom: open ? "1px dashed rgba(0,240,255,0.30)" : "none" }}
      >
        <div className="flex items-center gap-3">
          <span className="font-mono text-[12px] uppercase tracking-[0.32em]"
                style={{ color: "var(--cyan)", textShadow: "0 0 10px rgba(0,240,255,0.55)" }}>
            SYSTEM STATUS
          </span>
          <span className="inline-block w-2 h-4 align-middle breathe" style={{ background: "var(--cyan)", boxShadow: "0 0 8px var(--cyan)" }} />
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--text-3)]">
          {open ? "▾ COLLAPSE" : "▸ EXPAND"} · {ts}
        </span>
      </button>

      {open && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 p-5 sm:p-7 font-mono text-[12px]" style={{ fontFamily: "'IBM Plex Mono', 'JetBrains Mono', 'Fira Code', monospace" }}>
          {/* Node Status */}
          <div data-testid="dash-nodes" style={{ background: "rgba(7,4,28,0.55)", border: "1px solid rgba(0,240,255,0.18)", borderRadius: 8, padding: 14 }}>
            <div className="text-[10px] uppercase tracking-[0.30em] mb-2.5" style={{ color: "var(--cyan)" }}>
              ◈ NODE STATUS
            </div>
            <div className="space-y-1.5">
              {NODES_SEED.map(n => (
                <div key={n.name} className="flex items-center justify-between">
                  <span className="text-[var(--text)]">{n.name}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-[var(--text-3)] text-[10px]">{n.domain}</span>
                    <span className="w-2 h-2 rounded-full"
                          style={{ background: STATUS_DOT[n.status].color, boxShadow: `0 0 6px ${STATUS_DOT[n.status].color}` }} />
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Available Minds */}
          <div data-testid="dash-minds" style={{ background: "rgba(7,4,28,0.55)", border: "1px solid rgba(255,0,255,0.18)", borderRadius: 8, padding: 14 }}>
            <div className="text-[10px] uppercase tracking-[0.30em] mb-2.5" style={{ color: "var(--magenta)" }}>
              ◉ AVAILABLE MINDS
            </div>
            <div className="space-y-1.5">
              {MINDS_SEED.map(m => (
                <div key={m.name} className="flex items-center justify-between">
                  <span className="text-[var(--text)]">{m.name}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded"
                        style={{ color: m.color, border: `1px solid ${m.color}`, background: "rgba(0,0,0,0.3)" }}>
                    {m.tag}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Federation Wire */}
          <div data-testid="dash-wire" style={{ background: "rgba(7,4,28,0.55)", border: "1px solid rgba(255,181,62,0.18)", borderRadius: 8, padding: 14 }}>
            <div className="text-[10px] uppercase tracking-[0.30em] mb-2.5" style={{ color: "var(--gold)" }}>
              📡 FEDERATION WIRE
            </div>
            <div className="space-y-2">
              {wire.length === 0 && (
                <div className="text-[var(--text-3)] italic">No wire activity in this environment.</div>
              )}
              {wire.map((w, i) => (
                <div key={i} className="text-[var(--text-2)] leading-snug">
                  <span style={{ color: "var(--gold)" }}>[{w.kind || "tx"}]</span>{" "}
                  <span className="text-[var(--text)]">{(w.summary || "").slice(0, 80)}</span>
                </div>
              ))}
            </div>
          </div>
          {/* The Recycler · entropy management · SFUI-004 Rev 3 MAGENTA spectral */}
          <div
            data-testid="dash-recycler"
            style={{
              background:    "rgba(7,4,28,0.55)",
              border:        "1px solid rgba(255,43,214,0.30)",
              borderRadius:  8,
              padding:       14,
              boxShadow:     "inset 0 0 18px rgba(255,43,214,0.10)",
            }}
          >
            <div className="text-[10px] uppercase tracking-[0.30em] mb-2.5" style={{ color: "#ff2bd6" }}>
              ♻️ THE RECYCLER · ENTROPY
            </div>
            <div className="flex items-center gap-3 mb-3">
              <img
                src={RECYCLER_FACE_URL}
                alt={RECYCLER_ALT}
                data-spectral-role="magenta"
                style={{
                  width:        44,
                  height:       44,
                  borderRadius: "50%",
                  objectFit:    "cover",
                  border:       "2px solid rgba(255,43,214,0.85)",
                  boxShadow:    "0 0 14px rgba(255,43,214,0.55)",
                  filter:       RECYCLER_FILTER,
                  flexShrink:   0,
                }}
              />
              <div className="leading-tight">
                <div className="text-[var(--text)]">The Recycler</div>
                <div className="text-[9px] uppercase tracking-[0.22em]" style={{ color: "rgba(255,43,214,0.85)" }}>
                  Lifecycle · Renewal
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[var(--text-3)]">Status</span>
                <span style={{ color: "#ff2bd6" }}>RUNNING</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[var(--text-3)]">Node sweep</span>
                <span className="text-[var(--text)]">7m ago</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[var(--text-3)]">Entropy</span>
                <span className="text-[var(--text)]">0.18</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
